import React, { useState, useEffect } from 'react';
import { Room, RoomEvent, DataPacket_Kind } from 'livekit-client';
import '../../styles/streaming.css';

interface ReactionOverlayProps {
  room: Room;
  showButtons?: boolean;
  showBubbles?: boolean;
}

// Helper to generate random positions for reactions
const getRandomPosition = () => {
  // Generate positions across more of the screen width
  const x = Math.floor(Math.random() * 80) + 10; // 10% to 90% of width
  return { x };
};

export const ReactionOverlay: React.FC<ReactionOverlayProps> = ({
  room,
  showButtons = true,
  showBubbles = true,
}) => {
  const [reacs, setReacs] = useState<{ id: string; emoji: string; position: { x: number } }[]>([]);

  useEffect(() => {
    // Handler for incoming data messages
    const handleDataReceived = (payload: Uint8Array, participant?: any) => {
      try {
        const decoder = new TextDecoder();
        const msgText = decoder.decode(payload);
        const msg = JSON.parse(msgText);
        
        console.log('[ReactionOverlay] Recibido mensaje de datos:', msg);
        
        if (msg.type === 'reaction') {
          // Log más detallado para diagnosticar el problema
          console.log('[ReactionOverlay] Reacción recibida:', {
            emoji: msg.emoji,
            from: participant?.identity || 'unknown',
            showBubbles,
            currentReactions: reacs.length
          });
          
          // Solo procesar si estamos mostrando burbujas
          if (showBubbles) {
            // Crear un ID único para esta reacción
            const id = Date.now().toString() + Math.random().toString(36).substring(2, 9);
            // Obtener posición aleatoria
            const position = getRandomPosition();
            
            // Registrar la información de la nueva reacción
            console.log(`[ReactionOverlay] Añadiendo reacción: ${msg.emoji} en posición x:${position.x}%, ID:${id}`);
            
            // Añadir la reacción al estado
            setReacs(prev => {
              const newReacs = [...prev, { id, emoji: msg.emoji, position }];
              console.log(`[ReactionOverlay] Total de reacciones activas: ${newReacs.length}`);
              return newReacs;
            });
            
            // Eliminar la reacción después de la animación
            setTimeout(() => {
              setReacs(prev => {
                const filtered = prev.filter(r => r.id !== id);
                console.log(`[ReactionOverlay] Eliminando reacción ${id}, quedan ${filtered.length}`);
                return filtered;
              });
            }, 3000);
          } else {
            console.log('[ReactionOverlay] No mostrando burbujas ya que showBubbles=false');
          }
        }
      } catch (err) { 
        console.error('[ReactionOverlay] Error procesando mensaje:', err);
      }
    };
    
    // Log detallado de la configuración del listener
    console.log('[ReactionOverlay] Configurando listener DataReceived, showBubbles:', showBubbles, 'showButtons:', showButtons);
    
    // Asegurarse de eliminar cualquier listener previo antes de añadir uno nuevo
    room.off(RoomEvent.DataReceived, handleDataReceived);
    room.on(RoomEvent.DataReceived, handleDataReceived);
    
    return () => {
      console.log('[ReactionOverlay] Eliminando listener DataReceived');
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, showBubbles, reacs.length]); // Añadido reacs.length para ver cambios en el estado

  const send = (emoji: string) => {
    try {
      console.log('[ReactionOverlay] Enviando reacción:', emoji);
      
      // Crear el mensaje con formato correcto
      const message = JSON.stringify({ type: 'reaction', emoji });
      
      // Enviar usando RELIABLE para asegurar la entrega
      room.localParticipant.publishData(
        new TextEncoder().encode(message),
        DataPacket_Kind.RELIABLE
      );
      
      // Mensaje de confirmación
      console.log('[ReactionOverlay] Reacción enviada correctamente');
    } catch (err) {
      console.error('[ReactionOverlay] Error enviando reacción:', err);
    }
  };

  return (
    <>
      {/* Contenedor principal de reacciones (burbujas) */}
      {showBubbles && (
        <div className="reaction-container">
          {reacs.map(r => (
            <div 
              key={r.id} 
              className="reaction-overlay"
              style={{ 
                left: `${r.position.x}%`,
                bottom: '0'
              }}
            >
              {r.emoji}
            </div>
          ))}
        </div>
      )}
      
      {/* Botones de reacción */}
      {showButtons && (
        <div className="reaction-buttons">
          {['👍','❤️','😂','🎉'].map(em => (
            <button
              key={em}
              onClick={() => send(em)}
              className="text-2xl reaction-button"
              title="Send reaction"
            >
              {em}
            </button>
          ))}
        </div>
      )}
    </>
  );
};
